import * as React from "react";
import {
  BottomBar,
  ConfirmSheet,
  EntityRow,
  OutlookHeader,
  SectionLabel,
  cls,
} from "./components";
import { CONN_TABS, ConnTab, PRIMARY_TAB_KINDS } from "./connTabs";
import { Icon } from "./icons";
import { createNewCopy } from "./search";
import {
  fetchRecentEntities,
  searchAll as gqlSearchAll,
  searchEntities as gqlSearchEntities,
} from "./api/client";
import { entityFromLinkable } from "./api/mappers";
import type { EntityKind as GqlEntityKind } from "./api/types";
import { useSession } from "./SessionContext";
import type { EmailContext, Entity } from "./types";

/**
 * Map a ConnTab value to the backend's EntityKind enum where one exists.
 * "all" and "other" have no single-kind equivalent — caller routes
 * those to search_all / fetchRecentEntities-without-kind and filters
 * client-side as needed.
 */
function tabToGqlKind(tab: ConnTab): GqlEntityKind | undefined {
  switch (tab) {
    case "case": return "CASE";
    case "account": return "ACCOUNT";
    case "business": return "BUSINESS";
    default: return undefined;
  }
}

/** Header for the empty-query default list — same regardless of active type chip. */
const RECENT_LABEL = "Recent records";

/** Per-tab UI affordances — emoji on the chip + placeholder in the search box. */
const TAB_DISPLAY: Record<ConnTab, { emoji: string; placeholder: string }> = {
  all: { emoji: "🔍", placeholder: "Search records…" },
  case: { emoji: "📁", placeholder: "Search cases…" },
  account: { emoji: "🏦", placeholder: "Search accounts…" },
  business: { emoji: "🏢", placeholder: "Search businesses…" },
  other: { emoji: "⋯", placeholder: "Search records…" },
};

export function AddConnectionSinglePage({
  email,
  epicEmailMessageId,
  existingConnectionEntityIds,
  initialTab = "all",
  onCancel,
  onConnect,
  onCreateCaseFromEmail,
}: {
  email: EmailContext;
  epicEmailMessageId: string | null;
  existingConnectionEntityIds: Set<string>;
  initialTab?: ConnTab;
  onCancel: () => void;
  onConnect: (entities: Entity[]) => Promise<void>;
  /** Server-side AI seeds the case from the email body — takes ~3-5s. */
  onCreateCaseFromEmail: () => Promise<void>;
}): JSX.Element {
  const session = useSession();
  const [type, setType] = React.useState<ConnTab>(initialTab);
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [recent, setRecent] = React.useState<Entity[]>([]);
  const [recentLoading, setRecentLoading] = React.useState(false);
  const [matches, setMatches] = React.useState<Entity[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  // Multi-select: keyed on entity id so toggling is O(1) and lookups are
  // independent of which list (recent vs matches) the entity came from.
  // The full Entity is stored as the value so selections survive chip /
  // query changes even when the row leaves the visible list.
  const [selectedEntities, setSelectedEntities] = React.useState<Map<string, Entity>>(
    new Map()
  );
  // True while bulk_create_connections is in flight. Disables the
  // BottomBar buttons and swaps the primary label to "Connecting…".
  // Selections are preserved on failure so the user can retry without
  // re-picking everything.
  const [isConnecting, setIsConnecting] = React.useState(false);
  // True while create_case_from_email is in flight. The server runs AI
  // analysis so this can take a few seconds; we disable the rest of
  // the page so the user can't kick off a parallel action.
  const [isCreatingCase, setIsCreatingCase] = React.useState(false);
  // Two-step guard for the AI create: the create-row only opens this
  // confirm sheet — the mutation fires from the sheet's Create button.
  // Stops an accidental tap from kicking off a ~3-5s AI run + junk case.
  const [confirmCreate, setConfirmCreate] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const switchType = (k: ConnTab) => {
    // Preserve query AND selections across chip changes — selections
    // are a user-built list, not tied to a single search scope.
    setType(k);
  };

  const toggleSelect = (e: Entity) => {
    setSelectedEntities((prev) => {
      const next = new Map(prev);
      if (next.has(e.id)) next.delete(e.id);
      else next.set(e.id, e);
      return next;
    });
  };

  const handleConnect = async () => {
    if (selectedEntities.size === 0 || isConnecting) return;
    setIsConnecting(true);
    try {
      await onConnect(Array.from(selectedEntities.values()));
      // Success — parent navigates away, AddConnection unmounts.
    } catch {
      // App.handleConnect already toasts the error. Keep selections so
      // the user can retry, just exit the loading state.
      setIsConnecting(false);
    }
  };

  const handleCreateCase = async () => {
    if (isCreatingCase || isConnecting) return;
    setIsCreatingCase(true);
    try {
      await onCreateCaseFromEmail();
      // Success — parent navigates away, AddConnection unmounts.
    } catch {
      // App.handleCreateCaseFromEmail already toasts the error.
      setIsCreatingCase(false);
    }
  };

  // Debounce the query input.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Empty-query default state: show the user's most-recently-viewed
  // records, server-filtered to the active tab where possible.
  //   • "All"   → no kind filter, return 10 of any kind.
  //   • Case / Account / Business → server filters via kind arg.
  //   • "Other" → no single-kind arg fits "not-primary"; fetch 30
  //     unfiltered and drop primary kinds client-side. (Backend
  //     could add an `excluded_kinds` / `kind_in` arg to do this
  //     server-side; leaving as a follow-up.)
  React.useEffect(() => {
    if (!session) {
      setRecent([]);
      return;
    }
    if (debouncedQuery.trim() !== "") return;
    let cancelled = false;
    // Clear stale list + show skeleton immediately on tab switch so
    // the user doesn't see the previous tab's records lingering while
    // the new query is in flight.
    setRecent([]);
    setRecentLoading(true);
    (async () => {
      try {
        const kind = tabToGqlKind(type);
        const limit = type === "other" ? 30 : 10;
        const result = await fetchRecentEntities(
          session.accessToken,
          limit,
          session.refreshSession,
          kind
        );
        if (cancelled) return;
        let mapped = result
          .map((r) => entityFromLinkable(r.interactable))
          .filter((e): e is Entity => !!e);
        if (type === "other") {
          mapped = mapped
            .filter((e) => !PRIMARY_TAB_KINDS.includes(e.kind))
            .slice(0, 10);
        }
        setRecent(mapped);
      } catch (e: any) {
        if (!cancelled) console.warn("recent_entities failed:", e?.message ?? e);
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, session?.accessToken, type]);

  // Fetch search results when query is non-empty.
  React.useEffect(() => {
    const q = debouncedQuery.trim();
    if (!session || q === "") {
      setMatches([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSearchError(null);
    (async () => {
      try {
        // Tab dispatch:
        //   • "all"    → search_all (every Linkable in parallel)
        //   • Case / Account / Business → per-type search_entities
        //   • "other"  → search_all + drop primary kinds client-side
        //                (no single-kind arg fits "not-primary")
        const gqlKind = tabToGqlKind(type);
        let result;
        if (gqlKind) {
          result = await gqlSearchEntities(
            gqlKind,
            q,
            session.accessToken,
            25,
            session.refreshSession
          );
        } else {
          result = await gqlSearchAll(q, session.accessToken, 25, session.refreshSession);
        }
        if (cancelled) return;
        let mapped = result.map(entityFromLinkable).filter((e): e is Entity => !!e);
        if (type === "other") {
          mapped = mapped.filter((e) => !PRIMARY_TAB_KINDS.includes(e.kind));
        }
        setMatches(mapped);
      } catch (e: any) {
        if (!cancelled) {
          setSearchError(e?.message ?? "Search failed");
          setMatches([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type, debouncedQuery, session?.accessToken]);

  // Create-new affordance is only wired for cases right now — other
  // kinds need their own creation flows before they earn a row.
  const createCopy = type === "case" ? createNewCopy(email) : null;
  const matchCount = matches.length;
  const hasQuery = debouncedQuery.trim() !== "";

  return (
    <>
      <OutlookHeader
        leadingKind="back"
        title="Add connection"
        // subtitle={email.subject} // CLUADE: No subtitle needed.
        onLeading={onCancel}
      />
      <div className="pane">
        <SectionLabel title="Type" />
        <div className="chip-strip-wrap">
          <div className="chip-strip">
            {CONN_TABS.map((tab) => (
              <button
                key={tab.key}
                className={cls("type-chip", type === tab.key && "on")}
                onClick={() => switchType(tab.key)}
              >
                <span className="chip-emoji" aria-hidden="true">{TAB_DISPLAY[tab.key].emoji}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <SectionLabel
          title="Record"
          count={
            hasQuery
              ? loading
                ? "searching…"
                : `${matchCount} match${matchCount === 1 ? "" : "es"}`
              : undefined
          }
        />

        <div className="search-bar">
          <span className="icn"><Icon.Search /></span>
          <input
            ref={inputRef}
            type="text"
            placeholder={TAB_DISPLAY[type].placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="clear" onClick={() => setQuery("")} aria-label="Clear">
              ✕
            </button>
          )}
        </div>

        <div className="card">
          {createCopy && (
            <button
              className={cls("create-row", isCreatingCase && "loading")}
              onClick={() => setConfirmCreate(true)}
              disabled={isCreatingCase || isConnecting}
            >
              <span className="plus"><Icon.Plus /></span>
              <div className="text">
                <div className="label">
                  {isCreatingCase ? "Creating case…" : createCopy.label}
                </div>
                <div className="hint">
                  {isCreatingCase
                    ? "Running AI analysis on the email body"
                    : createCopy.hint}
                </div>
              </div>
              <span className="chev"><Icon.Chevron /></span>
            </button>
          )}

          {!hasQuery && recentLoading && (
            <>
              <div className="group-label">{RECENT_LABEL}</div>
              <SearchSkeletonRow />
              <SearchSkeletonRow />
              <SearchSkeletonRow />
            </>
          )}

          {!hasQuery && !recentLoading && recent.length > 0 && (
            <>
              <div className="group-label">{RECENT_LABEL}</div>
              {recent.map((e) => {
                const linked = existingConnectionEntityIds.has(e.id);
                return (
                  <EntityRow
                    key={e.id}
                    entity={e}
                    alreadyLinked={linked}
                    selected={!linked && selectedEntities.has(e.id)}
                    onClick={() => !linked && toggleSelect(e)}
                    trailing={
                      linked ? undefined : (
                        <div className={cls("radio", selectedEntities.has(e.id) && "on")}>
                          {selectedEntities.has(e.id) && <Icon.Check />}
                        </div>
                      )
                    }
                  />
                );
              })}
            </>
          )}

          {hasQuery && loading && (
            <>
              <div className="group-label">Searching…</div>
              <SearchSkeletonRow />
              <SearchSkeletonRow />
              <SearchSkeletonRow />
            </>
          )}

          {hasQuery && !loading && matches.length > 0 && (
            <>
              <div className="group-label">
                {matchCount} match{matchCount === 1 ? "" : "es"}
              </div>
              {matches.map((e) => {
                const linked = existingConnectionEntityIds.has(e.id);
                return (
                  <EntityRow
                    key={e.id}
                    entity={e}
                    alreadyLinked={linked}
                    selected={!linked && selectedEntities.has(e.id)}
                    onClick={() => !linked && toggleSelect(e)}
                    trailing={
                      linked ? undefined : (
                        <div className={cls("radio", selectedEntities.has(e.id) && "on")}>
                          {selectedEntities.has(e.id) && <Icon.Check />}
                        </div>
                      )
                    }
                  />
                );
              })}
            </>
          )}

          {searchError && hasQuery && (
            <div
              style={{
                padding: "16px 14px",
                fontSize: 12,
                color: "var(--urgent)",
              }}
            >
              {searchError}
            </div>
          )}

          {!loading && !searchError && hasQuery && matches.length === 0 && (
            <div
              style={{
                padding: "24px 14px",
                textAlign: "center",
                fontSize: 12,
                color: "var(--ios-secondary)",
              }}
            >
              {type === "all" || type === "other"
                ? `No matches for "${debouncedQuery}".`
                : `No ${(CONN_TABS.find((t) => t.key === type)?.label ?? "")
                    .toLowerCase()} match "${debouncedQuery}".`}
            </div>
          )}

          {!hasQuery && !recentLoading && recent.length === 0 && (
            <div
              style={{
                padding: "24px 14px",
                textAlign: "center",
                fontSize: 12,
                color: "var(--ios-secondary)",
              }}
            >
              Type to search.
            </div>
          )}
        </div>
      </div>
      <BottomBar
        secondaryLabel="Cancel"
        secondaryOnClick={onCancel}
        primaryLabel={
          isConnecting
            ? "Connecting…"
            : selectedEntities.size > 1
              ? `Connect (${selectedEntities.size})`
              : "Connect"
        }
        primaryDisabled={
          selectedEntities.size === 0 || isConnecting || isCreatingCase
        }
        primaryOnClick={handleConnect}
      />
      {confirmCreate && (
        <ConfirmSheet
          title="Create new case?"
          body="Runs AI on the email body (~3–5s) and adds a new case to EPIC."
          confirmLabel="Create"
          busyLabel="Creating…"
          busy={isCreatingCase}
          onConfirm={handleCreateCase}
          onCancel={() => setConfirmCreate(false)}
        />
      )}
    </>
  );
}

/**
 * Placeholder row matching the .row shape — shown while a search is
 * in flight so the result card isn't empty between keystrokes and
 * responses.
 */
function SearchSkeletonRow(): JSX.Element {
  return (
    <div className="row skeleton" aria-hidden="true">
      <span className="type-emoji">
        <span className="skel-bar" style={{ width: 22, height: 22, borderRadius: 6 }} />
      </span>
      <div className="row-info">
        <span className="skel-bar" style={{ width: "55%", height: 13, display: "block" }} />
        <span className="skel-bar" style={{ width: "35%", height: 11, display: "block", marginTop: 6 }} />
      </div>
    </div>
  );
}
